import {
  Clock,
  GitBranch,
  LayoutGrid,
  Network,
  SearchCheck,
  Swords,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

/**
 * The dashboard's navigation, as data.
 *
 * One model drives the desktop rail AND the mobile drawer, so a destination
 * can never exist on one and be unreachable on the other. Before this, the
 * sidebar was `hidden md:flex` with no mobile replacement, which made six of
 * the seven tabs unreachable on a phone.
 *
 * Consolidated per the approved design: five primary destinations, with the
 * agency surfaces (All Domains, Competitors, Fix Threads) grouped under
 * "Compare" rather than deleted.
 *
 * Icons are lucide, which is the same 24x24 outline family the radar-v2 mock
 * draws in — its Overview glyph is `LayoutGrid` node-for-node. Where the mock
 * hand-composited a glyph, the closest CANONICAL lucide icon is used instead
 * of copying the one-off path: a bespoke SVG here would be an icon nobody
 * maintains, sitting in a set of 1,600 that someone does.
 */

export type RadarTab =
  | 'overview'
  | 'actions'
  | 'details'
  | 'history'
  | 'competitor'
  | 'threads'
  | 'domains'

export interface NavItem {
  tab: RadarTab
  label: string
  icon: LucideIcon
  /** Count rendered as a chip. Omitted or 0 renders nothing. */
  badge?: number
  /** Chip colour; defaults to the muted instrument tone. */
  badgeTone?: 'action' | 'neutral'
}

export interface NavSection {
  /** Undefined for the primary group, which renders without a heading. */
  label?: string
  items: NavItem[]
}

export function buildNavModel({
  actionCount,
  savedRunCount,
  threadCount,
}: {
  actionCount: number
  savedRunCount: number
  threadCount: number
}): NavSection[] {
  return [
    {
      items: [
        { tab: 'overview', label: 'Dashboard', icon: LayoutGrid },
        {
          tab: 'actions',
          label: 'Actions',
          icon: Wrench,
          badge: actionCount,
          badgeTone: 'action',
        },
        { tab: 'details', label: 'Tool details', icon: SearchCheck },
        {
          tab: 'history',
          label: 'History',
          icon: Clock,
          badge: savedRunCount,
        },
      ],
    },
    {
      label: 'Compare',
      items: [
        { tab: 'competitor', label: 'Competitors', icon: Swords },
        { tab: 'domains', label: 'All domains', icon: Network },
        {
          tab: 'threads',
          label: 'Fix threads',
          icon: GitBranch,
          badge: threadCount,
        },
      ],
    },
  ]
}

/** Flat list, for the mobile select and keyboard shortcuts. */
export function flattenNav(sections: NavSection[]): NavItem[] {
  return sections.flatMap(s => s.items)
}
